import { subDays, subMonths, subYears } from "date-fns"
import { JournalAPI, UserReportAPI } from "generated-api"
import { IChartDataSet } from "ui/configuration/model"
import { ITimeFilter } from "ui/model"

export const REPORTS_RESULT_LIST_DOM_ID = "reports-result-list"
export const REPORTS_MY_LIST_DOM_ID = "reports-my-list"

export const REPORTS_SORTABLE_ITEM_DEFAULT_CLASSNAME = "reports-sortable-default-item"
export const REPORTS_SORTABLE_ITEM_DRAGGED_CLASSNAME = "reports-sortable-dragged-item"

export const REPORT_PARAMETERS_MENU_OPENED_WIDTH_IN_REM = "40rem"
export const REPORT_PARAMETERS_MENU_CLOSED_WIDTH_IN_REM = "5rem"
export const REPORT_CREATE_HEADER_HEIGHT_IN_REM = "4rem"

export type IGetReportResultDTO = Awaited<ReturnType<typeof JournalAPI.getApiJournal>>
export type IGetReportDataResult = Awaited<ReturnType<typeof JournalAPI.getApiJournal>>["data"][0]
export type ICreateUserReportParams = Parameters<typeof UserReportAPI.postApiUserReport>[0]
export type IUpdateUserReportParams = Parameters<typeof UserReportAPI.putApiUserReport>[0]
export type IUserReport = Awaited<ReturnType<typeof UserReportAPI.getApiUserReportList>>[0]
export type IReportFilters = NonNullable<Awaited<ReturnType<typeof UserReportAPI.getApiUserReportList>>[0]["filters"]>
export type IReportGroupSortBy = Awaited<ReturnType<typeof UserReportAPI.getApiUserReportList>>[0]["groupBys"][0]
export type IReportResultDTO = Awaited<ReturnType<typeof JournalAPI.getApiJournal>>["data"][0]
export type IReportTotal = Awaited<ReturnType<typeof JournalAPI.getApiJournal>>["totals"]

export interface IReportResult extends IReportResultDTO {
  id: number
}

export interface IGetReportResult extends IGetReportResultDTO {
  data: IReportResult[]
}

export type IReportsRepositoryResult = IReportResultDTO
export interface IReportsRepositoryResults {
  bids: number
  buys: number
  delivery_fees: number
  screen_count: number
  ecpm: number
  impressions: number
  media_count: number
  spend: number
  ssp_spend: number
  wins: number
}

export const LESS_THAN_ONE_DAY_FILTER_KEY = "less.than.1.day"
export const LESS_THAN_15_DAYS_FILTER_KEY = "less.than.15.days"
export const LESS_THAN_ONE_MONTH_FILTER_KEY = "less.than.1.month"
export const LESS_THAN_3_MONTHS_FILTER_KEY = "less.than.3.months"
export const LESS_THAN_SIX_MONTH_FILTER_KEY = "less.than.6.months"
export const LESS_THAN_ONE_YEAR_FILTER_KEY = "less.than.1.year"

const REVENUE_TIME_FILTERS: ITimeFilter[] = [
  { date: subDays(new Date(), 1), labelKey: LESS_THAN_ONE_DAY_FILTER_KEY },
  { date: subDays(new Date(), 15), labelKey: LESS_THAN_15_DAYS_FILTER_KEY },
  { date: subMonths(new Date(), 1), labelKey: LESS_THAN_ONE_MONTH_FILTER_KEY },
  { date: subMonths(new Date(), 3), labelKey: LESS_THAN_3_MONTHS_FILTER_KEY },
  { date: subMonths(new Date(), 6), labelKey: LESS_THAN_SIX_MONTH_FILTER_KEY },
  { date: subYears(new Date(), 1), labelKey: LESS_THAN_ONE_YEAR_FILTER_KEY }
]
export const getDefaultRevenueTimeFilter = (): ITimeFilter => REVENUE_TIME_FILTERS[2]
export const getRevenueTimeFilters = (): ITimeFilter[] => REVENUE_TIME_FILTERS.slice()

export const ALL_REPORTS_GROUP_SORT_BYS: IReportGroupSortBy[] = [
  "campaign_id",
  "order_id",
  "ssp_id",
  "publisher_id",
  "venue_type_id",
  "media_id",
  "screen_id",
  "media_dimension",
  "media_is_portrait",
  "media_is_video",
  "weather_id",
  "month",
  "day",
  "week",
  "hour",
  "campaign_coordinate_id",
  "country",
  "state",
  "city",
  "postal_code"
]
export const DEFAULT_REPORTS_RESULT_COLUMN_SORT: string[] = [
  "campaign_name",
  "order_id",
  "order_label",
  "order_name",
  "ssp_id",
  "publisher_id",
  "venue_type_id",
  "media_id",
  "media_name",
  "media_count",
  "screen_count",
  "screen_id",
  "screen_name",
  "media_dimension",
  "media_is_portrait",
  "media_is_video",
  "campaign_coordinate_id",
  "weather_id",
  "country",
  "state",
  "city",
  "postal_code",
  "lat",
  "lon",
  "campaign_coordinate_name",
  "campaign_coordinate_lat",
  "campaign_coordinate_lon",
  "campaign_coordinate_radius",
  "hour",
  "day",
  "week",
  "month",
  "bids",
  "wins",
  "impressions",
  "buys",
  "spend",
  "ssp_spend",
  "delivery_fees",
  "ecpm"
]

export const REPORTS_HIDDEN_COLUMN_KEYS = [
  "id",
  "campaign_id",
  "campaign_coordinate_id",
  "venue_type_name",
  "screen_id",
  "order_id",
  "media_id",
  "order_label"
]

export enum REVENU_GROUP_BYS {
  HOUR = "hour",
  DAY = "day",
  WEEK = "week",
  MONTH = "month"
}

export interface IReportServiceState {
  applyTimeout: any
  currentLaunchRequestId: number
}

export interface IReportParameters {
  filters: IReportFilters
  groupBys: IReportGroupSortBy[]
  sortBys: IReportGroupSortBy[]
  name: string
  description: string
}

export interface IReportForm {
  report: IUserReport | null
  parameters: IReportParameters
  results: IReportResult[] | null
  isResultLoading: boolean
  hasResultLoadingError: boolean
  isParametersPanelOpened: boolean
  isCreation: boolean
  isUpdating: boolean
  updateFormError: string
}

export interface IRevenueState {
  isLoading: boolean
  hasLoadingError: boolean
  chartDataSets: IChartDataSet[]
  total: IReportTotal | null
}

export interface IReportsState {
  createForm: IReportForm
  editForm: IReportForm
  isListLoading: boolean
  hasListLoadingError: boolean
  userReports: IUserReport[] | null
  revenue: IRevenueState
}

export interface IReportsRepository {
  getResults(parameters: IReportParameters): Promise<IGetReportResult>
  getCampaignsResults(campaignIds: number[]): Promise<IReportsCampaignResult[]>
  getSSPOperatorResults(campaignId: number): Promise<IReportsSSPOperatorResult[]>
  getCreativeResults(campaignId: number): Promise<IReportsCreativeResult[]>
  getShapeResults(campaignId: number): Promise<IReportsShapeResult[]>
  getCoordinateResults(campaignId: number): Promise<IReportsCoordinateResult[]>
  getMyUserReports(): Promise<IUserReport[]>
  createUserReport(params: ICreateUserReportParams): Promise<IUserReport>
  updateUserReport(params: IUpdateUserReportParams): Promise<IUserReport>
  delUserReport(reportId: number): Promise<IUserReport>
}

export interface IReportsCampaignResult extends IReportsRepositoryResult {
  campaign_id: number
}

export interface IReportsSSPOperatorResult extends IReportsRepositoryResult {
  ssp_id: number
  publisher_id: string // name of the publisher ...
}

export interface IReportsCreativeResult extends IReportsRepositoryResult {
  media_id: number
  media_name: string | undefined
  media_dimension: string
  media_is_portrait: boolean
  media_is_video: boolean
}

export interface IReportsShapeResult extends IReportsRepositoryResult {
  country: string
  state: string
  city: string
  postal_code: string
  venue_type_id: number
  venue_type_name: string
}

export interface IReportsCoordinateResult extends IReportsRepositoryResult {
  campaign_coordinate_id: number
  campaign_coordinate_lat: number
  campaign_coordinate_lon: number
  campaign_coordinate_radius: number
  campaign_coordinate_name: string
}
